/**
 * Centralized Feature Flag Rollout Configuration
 * Single source of truth for all flag groupings, phases, and monitoring
 */

// ========================================
// HARDCODED FLAGS (Stable, Removed from System)
// ========================================
export const HARDCODED_FLAGS = {
  'ENABLE_LOCAL_DATE_FILTER': {
    hardcodedDate: '2025-09-15',
    reason: 'Stable 45 days, zero regressions',
    impact: '+100% date accuracy (local timezone)',
    files: ['dateFormatUtils.ts']
  },
  'ENABLE_UNIFIED_SUMMARY_LAYOUT': {
    hardcodedDate: '2025-09-15',
    reason: 'Stable 45 days, UX improvement confirmed',
    impact: 'Unified summary layout (top position)',
    files: ['Global*Table.tsx (5 files)']
  },
  'ENABLE_SPEC_ITEMS_COLUMN': {
    hardcodedDate: '2025-09-15',
    reason: 'Stable 45 days, no regressions',
    impact: 'Linked items column in specs',
    files: ['DraggableSpecsTable.tsx', 'SpecLinkedItemsCell.tsx', 'SpecsTable.tsx']
  },
  'ENABLE_GLOBAL_PO_TAB': {
    hardcodedDate: '2025-09-15',
    reason: 'Stable 45 days, core feature',
    impact: 'Global PO tab enabled',
    files: ['SubconsManagement.tsx']
  },
  'ENABLE_GLOBAL_BY_DATE_TAB': {
    hardcodedDate: '2025-09-15',
    reason: 'Stable 45 days, core feature',
    impact: 'Global By Date tab enabled',
    files: ['SubconsManagement.tsx']
  },
  'ENABLE_GLOBAL_BY_SUBCONS_TAB': {
    hardcodedDate: '2025-09-15',
    reason: 'Stable 45 days, core feature',
    impact: 'Global By Subcons tab enabled',
    files: ['SubconsManagement.tsx']
  },
  'ENABLE_GLOBAL_BY_DATE_FLAT_VIEW': {
    hardcodedDate: '2025-09-15',
    reason: 'Stable 45 days, performance verified',
    impact: 'Flat view for By Date tab',
    files: ['SubconsManagement.tsx']
  },
  
  // ✅ REMOVED 2026-04-30 (PR-B): STRICT_PERMISSION_UI HARDCODED entry (hardcoded true in consumer)
  // ✅ REMOVED 2026-04-30 (PR-D): ENABLE_VO_IN_MILESTONE_CALCULATION HARDCODED entry (hardcoded true in OrderPaymentMilestoneForm + PaymentMilestoneForm) — Phase 6 fully retired
  // ✅ REMOVED 2026-04-30 (PR-B): ENABLE_TIMEZONE_AWARE_FILTERING HARDCODED entry (hardcoded true in consumer)
  // ✅ REMOVED 2026-04-30 (PR-C): ENABLE_ENHANCED_MONITORING HARDCODED entry (zero-consumer)
  // ✅ REMOVED 2026-04-30 (PR-A): ENABLE_PROXIMITY_CONTEXT_MENU, ENABLE_SPEC_DUPLICATE_BUTTON
  
  // Week 1 - Projects Page (Hardcoded 2025-10-10)
  'ENABLE_CONSOLIDATED_PERMISSIONS': {
    hardcodedDate: '2025-10-10',
    reason: 'Stable 45+ days, zero regressions, -20% DB calls verified',
    impact: '-20% DB calls, +10% load time reduction',
    files: ['ProjectsPage.tsx', 'useProjectPermissions.ts', 'Header.tsx', 'useConsolidatedPermissionsAdapter.tsx']
  },
  'ENABLE_CURSOR_PAGINATION': {
    hardcodedDate: '2025-10-10',
    reason: 'Stable 45+ days, pagination stable, -25% DB calls',
    impact: '-25% DB calls, +15% load time reduction',
    files: ['ProjectsPage.tsx', 'useCursorPagination.tsx']
  },
  'ENABLE_SMART_PRELOADING': {
    hardcodedDate: '2025-10-10',
    reason: 'Stable 45+ days, predictive loading working, -18% DB calls',
    impact: '-18% DB calls, +12% perceived performance',
    files: ['ProjectsPage.tsx', 'useSmartPreloading.tsx']
  },
  
  // Week 2 - Items/Build Page (Hardcoded 2025-10-10)
  'ENABLE_TIMELINE_VIRTUALIZATION': {
    hardcodedDate: '2025-10-10',
    reason: 'Stable 45+ days, handles 1000+ items smoothly',
    impact: '-70% render time for large timelines, +20% scroll FPS',
    files: ['ItemsBuildPage.tsx', 'TimelineVirtualized.tsx', 'useTimelineVirtualization.tsx']
  },
  'ENABLE_OPTIMIZED_FILTERS': {
    hardcodedDate: '2025-10-10',
    reason: 'Stable 45+ days, filter query time reduced by 22%',
    impact: '-22% DB calls, +18% filter response time',
    files: ['ItemsBuildPage.tsx', 'OptimizedFilters.tsx', 'useOptimizedFilters.tsx']
  },
  
  // Phase 2 - Subcons (Hardcoded 2025-10-10)
  'ENABLE_SUBCONS_PERMISSION_CONSOLIDATION': {
    hardcodedDate: '2025-10-10',
    reason: 'Stable 30+ days, -15% permission check time',
    impact: '-15% DB calls, +10% permission check speed',
    files: ['SubconsManagement.tsx', 'useSubconsPermissions.ts', 'useConsolidatedPermissionsAdapter.tsx']
  },
  
  // ✅ Tier 1 - Ổn định 60+ ngày (hardcoded 2025-11-09)
  'ENABLE_COMBINED_PROJECT_QUERY': {
    hardcodedDate: '2025-11-09',
    reason: 'Stable 60+ days, -30% DB calls, zero regressions',
    impact: '-30% DB calls, +25% load time reduction',
    files: ['useCombinedProjectQuery.tsx', 'ProjectDetailPage.tsx']
  },
  'ENABLE_PREDICTIVE_TABS': {
    hardcodedDate: '2025-11-09',
    reason: 'Stable 60+ days, predictive loading working, -15% load time',
    impact: '-15% perceived load time, +50% tab switch speed',
    files: ['usePredictiveTabs.tsx', 'ProjectDetailPage.tsx']
  },
  'ENABLE_SMART_PREFETCH': {
    hardcodedDate: '2025-11-09',
    reason: 'Stable 60+ days, -40% network requests, cache hit 85%+',
    impact: '-40% duplicate prefetch, +85% cache hit rate',
    files: ['useSmartPrefetch.tsx', 'ProjectDetailPage.tsx']
  },
  'ENABLE_OPTIMIZED_STAKEHOLDER_QUERIES': {
    hardcodedDate: '2025-11-09',
    reason: 'Stable 60+ days, -20% DB calls, shows notes/attachments',
    impact: '-20% DB calls, +15% query speed',
    files: ['useOptimizedStakeholderQueries.tsx', 'StakeholderManagement.tsx']
  },
  'ENABLE_REALTIME_BACKPRESSURE': {
    hardcodedDate: '2025-11-09',
    reason: 'Stable 60+ days, realtime stability, backpressure handling',
    impact: '+100% realtime stability, -30% event drops',
    files: ['realtimeSubscriptions.ts']
  },
  'ENABLE_BATCH_INVALIDATION': {
    hardcodedDate: '2025-11-09',
    reason: 'Stable 60+ days, batch cache invalidation, -25% cache thrashing',
    impact: '-25% cache operations, +40% cache efficiency',
    files: ['cacheInvalidation.ts']
  },
  'ENABLE_OPTIMIZED_REALTIME_SYNC': {
    hardcodedDate: '2025-11-09',
    reason: 'Stable 60+ days, optimized realtime sync, -20% sync latency',
    impact: '-20% sync latency, +30% sync reliability',
    files: ['realtimeSync.ts']
  },
  'ENABLE_WEEKLY_PLAN_OVERLAP_FILTER': {
    hardcodedDate: '2025-11-09',
    reason: 'Stable 60+ days, date accuracy, overlap-based filtering',
    impact: '+100% date accuracy, zero off-by-one errors',
    files: ['weeklyPlanUtils.ts', 'WeeklyPlan.tsx', 'PrintableDiaryLayout.tsx']
  },
  'ENABLE_SPECS_SYNC_BY_CODE': {
    hardcodedDate: '2025-11-09',
    reason: 'Stable 60+ days, ON by default, sync specs by code',
    impact: '+100% spec sync accuracy, zero orphaned files',
    files: ['SpecsContext.tsx', 'useDiarySpecs.tsx', 'SpecEditDialog.tsx']
  },
  'ENABLE_SPECS_REPORT_LAYOUT_FIX': {
    hardcodedDate: '2025-11-09',
    reason: 'Stable 60+ days, ON by default, report layout fix',
    impact: '+100% print layout accuracy, zero clipping',
    files: ['LayerKeyplanPreview.tsx']
  },
  'ENABLE_OPTIMIZED_LAYER_IMAGES': {
    hardcodedDate: '2025-11-09',
    reason: 'Stable 30+ days, -80% LCP, -90% mobile bandwidth, 100% implemented',
    impact: '-80% LCP, -90% mobile bandwidth, +95% optimization success',
    files: ['EnhancedLayerNameDialog.tsx', 'optimizeLayerImage.ts']
  },
  
  // ✅ Tier 2 - Production-Forced (hardcoded 2025-11-09)
  'ENABLE_CUSTOM_MILESTONES': {
    hardcodedDate: '2025-11-09',
    reason: 'Production-forced, stable 30+ days, core milestone system',
    impact: 'Custom milestone system, production-required feature',
    files: ['useMilestoneTypes.tsx', 'MilestoneTimeline.tsx']
  },
  
  // ✅ RETIRED 2026-04-30 (PR-F1): ENABLE_SITE_MODULE (hardcoded true in consumers)
  
  // ✅ Project Roles (hardcoded 2025-12-10)
  'ENABLE_PROJECT_ROLES': {
    hardcodedDate: '2025-12-10',
    reason: 'Project roles (SOL/PM/CD/QS/SM) in Resource tab - stable and needed for all users',
    impact: 'Project-specific roles visible in Team Management, filter by member in Home',
    files: ['ProjectFilters.tsx', 'ProjectTeamManagement.tsx']
  }
} as const;

// ========================================
// HARDCODED GROUPS (For Stable Tab Display)
// ========================================
export const HARDCODED_GROUPS = {
  'phase1': {
    title: '📦 Phase 1 Optimizations',
    description: 'Week 3 - Project Detail & Smart Prefetch',
    flags: ['ENABLE_COMBINED_PROJECT_QUERY', 'ENABLE_PREDICTIVE_TABS', 'ENABLE_SMART_PREFETCH']
  },
  'phase2': {
    title: '📦 Phase 2 Optimizations',
    description: 'Week 6 - Stakeholder Queries',
    flags: ['ENABLE_OPTIMIZED_STAKEHOLDER_QUERIES']
  },
  'phase5': {
    title: '📦 Phase 5 Infrastructure',
    description: 'Realtime Optimization (Backpressure, Batch, Sync)',
    flags: ['ENABLE_REALTIME_BACKPRESSURE', 'ENABLE_BATCH_INVALIDATION', 'ENABLE_OPTIMIZED_REALTIME_SYNC']
  },
  'diary_specs': {
    title: '📦 Diary & Specs',
    description: 'Weekly Plan + Specs Sync + Layout Fixes',
    flags: ['ENABLE_WEEKLY_PLAN_OVERLAP_FILTER', 'ENABLE_SPECS_SYNC_BY_CODE', 'ENABLE_SPECS_REPORT_LAYOUT_FIX']
  },
  'advanced': {
    title: '📦 Advanced Features',
    description: 'Milestones + Layer Image Optimization',
    flags: ['ENABLE_CUSTOM_MILESTONES', 'ENABLE_OPTIMIZED_LAYER_IMAGES']
  }
} as const;

// ========================================
// HR MODULE CONFIG
// ========================================
export const PHASE_HR_CONFIG = {
  id: 'hr-module',
  title: 'HR Module',
  description: 'Full HR Management System with 6-tab employee profile',
  flags: ['ENABLE_HR_MODULE'],
  kpis: [
    'Tab 1: Thông tin cá nhân (CCCD, địa chỉ, học vấn)',
    'Tab 2: Công việc (hợp đồng, kinh nghiệm)',
    'Tab 3: Lương & Thu nhập (RLS protected)',
    'Tab 4: Bảo hiểm (BHXH, BHYT)',
    'Tab 5: Gia đình (vợ/chồng, con cái)',
    'Tab 6: Tài sản (thiết bị công ty)'
  ],
  rollbackCriteria: ['Flag OFF → /hr routes hidden', 'No breaking changes to existing Roadmap'],
} as const;

// ========================================
// SYSTEM CONTROLS CONFIG
// ========================================
export const PHASE_SYSTEM_CONTROLS_CONFIG = {
  id: 'system-controls',
  title: 'System Controls',
  description: 'System-level controls for admin (Force Refresh, etc.)',
  flags: ['ENABLE_FORCE_REFRESH'],
  kpis: ['Admin can force refresh all connected users'],
  rollbackCriteria: ['Supabase broadcast không hoạt động', 'Performance issues'],
} as const;

// ========================================
// CONTROL CENTER CONFIG
// ========================================
export const PHASE_CONTROL_CENTER_CONFIG = {
  id: 'control-center',
  title: 'Control Center Phase 1: Activity Log',
  description: 'Activity Log - ghi nhận tự động mọi thay đổi qua DB triggers',
  flags: ['ENABLE_ACTIVITY_LOG'],
  kpis: ['Audit trail cho mọi thay đổi', 'Filter theo module/user/date'],
  rollbackCriteria: ['Flag OFF → tab Activity biến mất, triggers vẫn chạy'],
} as const;

// ========================================
// PHASE DEFINITIONS
// ========================================

export interface WeekConfig {
  week: number | string;
  phase: number | string;
  title: string;
  risk: 'lowest' | 'low' | 'medium' | 'higher';
  impact: string;
  flags: string[];
  monitoringDays: number;
  criticalMetrics: string[];
  passCriteria: Record<string, string>;
  enableAction?: string;
  disableAction?: string;
}

export const PHASE1_WEEKS: WeekConfig[] = [
  {
    week: 1,
    phase: 1,
    title: 'Projects Page',
    risk: 'lowest',
    impact: '-85% DB calls, -80% load time',
    flags: [
      'ENABLE_CONSOLIDATED_PERMISSIONS',
      'ENABLE_CURSOR_PAGINATION',
      'ENABLE_SMART_PRELOADING'
    ],
    monitoringDays: 5,
    criticalMetrics: ['avgResponseTime', 'successRate', 'dbQueryCount'],
    passCriteria: {
      avgResponseTime: '<=200ms',
      successRate: '>=95%',
      dbQueryCount: '<=50'
    },
    enableAction: 'enableWeek1',
    disableAction: 'disableWeek1'
  },
  {
    week: 2,
    phase: 1,
    title: 'Items/Build Page',
    risk: 'lowest',
    impact: '-70% render time, -30% memory',
    flags: [
      'ENABLE_TIMELINE_VIRTUALIZATION',
      'ENABLE_OPTIMIZED_FILTERS'
      // ✅ REMOVED 2026-04-30 (PR-C): 'ENABLE_ENHANCED_MONITORING'
    ],
    monitoringDays: 5,
    criticalMetrics: ['renderTime', 'scrollFPS', 'memoryUsage'],
    passCriteria: {
      renderTime: '<=1000ms',
      scrollFPS: '>=55',
      memoryUsage: '<=150MB'
    }
    // ✅ REMOVED 2026-04-30 (PR-C): enableAction/disableAction (Week 2 fully retired)
  },
  {
    week: 3,
    phase: 1,
    title: 'Project Detail',
    risk: 'low',
    impact: '-75% load time',
    flags: [
      'ENABLE_COMBINED_PROJECT_QUERY',
      'ENABLE_PREDICTIVE_TABS'
    ],
    monitoringDays: 5,
    criticalMetrics: ['loadTime', 'tabSwitchTime'],
    passCriteria: {
      loadTime: '<=500ms',
      tabSwitchTime: '<=100ms'
    },
    enableAction: 'enableWeek3',
    disableAction: 'disableWeek3'
  }
];

export const PHASE2_WEEKS: WeekConfig[] = [
  {
    week: 5,
    phase: 2,
    title: 'Maintenance Page',
    risk: 'medium',
    impact: '-65% query time',
    flags: [
      'ENABLE_OPTIMIZED_MAINTENANCE_STATE',
      'ENABLE_MAINTENANCE_PERMISSIONS',
      'ENABLE_MAINTENANCE_SMART_PAGINATION'
    ],
    monitoringDays: 7,
    criticalMetrics: ['avgResponseTime', 'successRate', 'cacheHitRate'],
    passCriteria: {
      avgResponseTime: '<=200ms',
      successRate: '>=95%',
      cacheHitRate: '>=80%'
    },
    enableAction: 'enableWeek5',
    disableAction: 'disableWeek5'
  },
  {
    week: 6,
    phase: 2,
    title: 'Stakeholders Page',
    risk: 'medium',
    impact: '-60% load time',
    flags: [
      'ENABLE_CONSOLIDATED_STAKEHOLDER_PERMISSIONS',
      'ENABLE_OPTIMIZED_STAKEHOLDER_QUERIES'
    ],
    monitoringDays: 7,
    criticalMetrics: ['loadTime', 'queryPayload', 'queryCount'],
    passCriteria: {
      loadTime: '<=500ms',
      queryPayload: '<=100KB',
      queryCount: '<=30'
    },
    enableAction: 'enableWeek6',
    disableAction: 'disableWeek6'
  },
  {
    week: 7,
    phase: 2,
    title: 'PublicPreview Page',
    risk: 'medium',
    impact: '-50% render time',
    flags: [
      'ENABLE_OPTIMIZED_PREVIEW_MODE',
      'ENABLE_PREVIEW_COMPONENTS'
    ],
    monitoringDays: 7,
    criticalMetrics: ['loadTime', 'renderTime'],
    passCriteria: {
      loadTime: '<=1000ms',
      renderTime: '<=800ms'
    },
    enableAction: 'enableWeek7',
    disableAction: 'disableWeek7'
  },
  {
    week: 8,
    phase: 2,
    title: 'Subcons Fine-tuning',
    risk: 'medium',
    impact: '-40% permission checks',
    flags: ['ENABLE_SUBCONS_PERMISSION_CONSOLIDATION'],
    monitoringDays: 7,
    criticalMetrics: ['queryCount', 'permissionCheckTime'],
    passCriteria: {
      queryCount: '<=40',
      permissionCheckTime: '<=50ms'
    },
    enableAction: 'enableWeek8',
    disableAction: 'disableWeek8'
  }
];

export const PHASE3_WEEKS: WeekConfig[] = [
  {
    week: 9,
    phase: 3,
    title: 'Admin Roles',
    risk: 'higher',
    impact: '-70% batch update time',
    flags: [
      // ✅ REMOVED 2026-04-30 (PR-E): 'ENABLE_ADMIN_ROLE_CONSOLIDATION' (hardcoded true)
      'ENABLE_BATCH_PERMISSION_UPDATES'
    ],
    monitoringDays: 7,
    criticalMetrics: ['batchUpdateTime', 'successRate'],
    passCriteria: {
      batchUpdateTime: '<=2000ms',
      successRate: '>=98%'
    },
    enableAction: 'enableWeek9',
    disableAction: 'disableWeek9'
  },
  {
    week: 10,
    phase: 3,
    title: 'Admin Matrix',
    risk: 'higher',
    impact: '-80% memory usage',
    flags: ['ENABLE_PERMISSION_MATRIX_VIRTUALIZATION'],
    monitoringDays: 7,
    criticalMetrics: ['renderTime', 'memoryUsage', 'scrollFPS'],
    passCriteria: {
      renderTime: '<=1500ms',
      memoryUsage: '<=200MB',
      scrollFPS: '>=50'
    },
    enableAction: 'enableWeek10',
    disableAction: 'disableWeek10'
  },
  {
    week: 11,
    phase: 3,
    title: 'Admin Projects',
    risk: 'higher',
    impact: '-60% filter time',
    flags: [
      'ENABLE_ADMIN_PROJECT_CONSOLIDATION',
      'ENABLE_ADMIN_FILTER_OPTIMIZATION'
    ],
    monitoringDays: 7,
    criticalMetrics: ['loadTime', 'filterResponseTime'],
    passCriteria: {
      loadTime: '<=800ms',
      filterResponseTime: '<=300ms'
    },
    enableAction: 'enableWeek11',
    disableAction: 'disableWeek11'
  },
  // ✅ RETIRED 2026-05-11 (PR-H1): week 12 block (sole flag ENABLE_ENHANCED_ADMIN_MONITORING orphan)
];

export const PHASE4_CONFIGS: WeekConfig[] = [
  {
    week: '4A',
    phase: '4A',
    title: 'High Priority Pages',
    risk: 'higher',
    impact: '-50% avg load time',
    flags: [
      'ENABLE_USER_MANAGEMENT_VIRTUALIZATION',
      'ENABLE_ADMIN_DASHBOARD_CONSOLIDATION',
      // ✅ RETIRED 2026-05-11 (PR-H1): 'ENABLE_ADMIN_STAKEHOLDER_QUERIES' (orphan)
      'ENABLE_INDEX_PAGE_OPTIMIZATION'
    ],
    monitoringDays: 7,
    criticalMetrics: ['loadTime', 'renderTime', 'queryCount'],
    passCriteria: {
      loadTime: '<=1000ms',
      renderTime: '<=800ms',
      queryCount: '<=60'
    },
    enableAction: 'enablePhase4A',
    disableAction: 'disablePhase4A'
  },
  {
    week: '4B',
    phase: '4B',
    title: 'Medium Priority Pages',
    risk: 'medium',
    impact: '+cache hit rate 80%',
    flags: [
      'ENABLE_CATEGORY_CACHING',
      'ENABLE_KEYPLAN_OPTIMIZATION',
      'ENABLE_SCHEDULE_LITE_VIRTUALIZATION'
    ],
    monitoringDays: 5,
    criticalMetrics: ['cacheHitRate', 'loadTime'],
    passCriteria: {
      cacheHitRate: '>=80%',
      loadTime: '<=1200ms'
    },
    enableAction: 'enablePhase4B',
    disableAction: 'disablePhase4B'
  }
];

// ========================================
// PHASE 5: INFRASTRUCTURE & BACKEND
// ========================================
export const PHASE5_CONFIGS: WeekConfig[] = [
  // 🪦 RETIRED 2026-05-02 (PR-G3): Phase 5A WeekConfig (5 procurement flags retired)
  {
    week: '5B',
    phase: '5B',
    title: 'I/O & Database Optimizations',
    risk: 'low',
    impact: '-30% payload size, +15% query speed',
    flags: [
      'ENABLE_SELECT_FIELD_TRIMMING',
      'ENABLE_SUBCONS_SMART_PAGINATION',
      'ENABLE_ITEMS_TAB_PAGINATION',
      'ENABLE_REALTIME_BACKPRESSURE',
      'ENABLE_BATCH_INVALIDATION',
      'ENABLE_OPTIMIZED_REALTIME_SYNC'
    ],
    monitoringDays: 5,
    criticalMetrics: ['payloadSize', 'queryTime', 'realtimeEvents'],
    passCriteria: {
      payloadSize: '<=50KB',
      queryTime: '<=200ms',
      realtimeEvents: '<=100/min'
    },
    enableAction: 'enablePhase5B',
    disableAction: 'disablePhase5B'
  },
  // ✅ REMOVED 2026-04-30 (PR-B): Phase 5C config (sole flag ENABLE_TIMEZONE_AWARE_FILTERING retired)
  {
    week: '5D',
    phase: '5D',
    title: 'Diary Weekly Plan - Overlap Filter',
    risk: 'low',
    impact: '+100% accuracy for weekly plan items display',
    flags: ['ENABLE_WEEKLY_PLAN_OVERLAP_FILTER'],
    monitoringDays: 3,
    criticalMetrics: ['weeklyPlanAccuracy', 'itemsDisplayCount'],
    passCriteria: {
      weeklyPlanAccuracy: '>=99%',
      itemsDisplayCount: 'matches Plan view'
    },
    enableAction: 'enablePhase5D',
    disableAction: 'disablePhase5D'
  }
];

// ========================================
// PHASE 6: LEGACY DIARY FLAGS — fully retired
// ========================================
// ✅ REMOVED 2026-04-30 (PR-D): PHASE6_CONFIGS export — sole flag ENABLE_VO_IN_MILESTONE_CALCULATION hardcoded true in consumers (Phase 6 fully retired)


// ========================================
// PHASE 7: PLAN PAGE OPTIMIZATIONS — fully retired
// ========================================
// 🪦 RETIRED 2026-05-02 (PR-G2): PHASE7_CONFIGS export removed — 5 Plan flags orphan from day 1, 0 consumers.

// ========================================
// P&L PERMISSIONS CONFIG
// ========================================
export const PHASE_PL_PERMISSIONS_CONFIG: WeekConfig = {
  week: 'PL-1',
  phase: 'PL',
  title: 'P&L Tab Permissions',
  risk: 'medium',
  impact: 'P&L tab visibility by permission',
  flags: ['ENABLE_PL_PERMISSIONS'],
  monitoringDays: 3,
  criticalMetrics: ['permission_check_accuracy', 'unauthorized_access_attempts'],
  passCriteria: {
    permission_check_accuracy: '>=99%',
    unauthorized_access_attempts: '0'
  },
  enableAction: 'enablePLPermissions',
  disableAction: 'disablePLPermissions'
};

// ========================================
// PHASE 8: ADMIN SECTION ENHANCEMENTS — fully retired
// ========================================
// 🪦 RETIRED 2026-05-02 (PR-G1): PHASE8_CONFIGS shrunk 13→6 flags (7 Admin V2 dead flags removed).
// 🪦 RETIRED 2026-05-04 (PR-G5): PHASE8_CONFIGS fully removed — 6 remaining flags retired, 0 consumers.

// ========================================
// PHASE ADMIN PERMISSION V2: Simple Permission Manager
// ========================================
export const PHASE_ADMIN_PERMISSION_V2_CONFIG: WeekConfig = {
  week: 'admin-v2',
  phase: 'admin-v2',
  title: 'Admin: Permission Manager V2',
  risk: 'medium',
  impact: 'UI-DB sync: 100%, Toggle latency -50%, Code complexity -63%',
  flags: ['ENABLE_SIMPLE_PERMISSION_MANAGER'],
  monitoringDays: 7,
  criticalMetrics: ['toggle_latency', 'sync_success_rate', 'ui_error_rate'],
  passCriteria: {
    toggle_latency: '<=200ms',
    sync_success_rate: '>=99%',
    ui_error_rate: '<=1%'
  },
  enableAction: 'enableAdminPermissionV2',
  disableAction: 'disableAdminPermissionV2'
};

// ========================================
// PHASE 22.1: USER VISIBILITY SIMPLIFICATION
// ========================================
export const PHASE22_CONFIGS: WeekConfig[] = [
  {
    week: '22.1',
    phase: '22',
    title: 'Phase 22.1: Simplified User Visibility',
    risk: 'medium',
    impact: 'Profile query latency -95% (500ms→<100ms), admin effort -98% (480 perms→5-10 exceptions)',
    flags: [
      'ENABLE_SIMPLIFIED_USER_VISIBILITY'
    ],
    monitoringDays: 7,
    criticalMetrics: [
      'profile_query_latency_p95',
      'cache_hit_rate',
      'hidden_users_count'
    ],
    passCriteria: {
      error_rate: '<0.1%',
      p95_latency: '<500ms',
      cache_hit_rate: '>=85%'
    },
    enableAction: 'enablePhase22',
    disableAction: 'disablePhase22'
  }
];

// ✅ REMOVED 2026-04-30 (PR-A): PHASE22_2_CONFIG (ENABLE_PRINT_PIN_LAYOUT_FIX dropped)

// ========================================
// UNIFIED MAPPING (for backwards compat with monitoringConfig.ts)
// ========================================
export const FLAG_WEEK_MAPPING = {
  week1: PHASE1_WEEKS[0],
  week2: PHASE1_WEEKS[1],
  week3: PHASE1_WEEKS[2],
  week5: PHASE2_WEEKS[0],
  week6: PHASE2_WEEKS[1],
  week7: PHASE2_WEEKS[2],
  week8: PHASE2_WEEKS[3],
  week9: PHASE3_WEEKS[0],
  week10: PHASE3_WEEKS[1],
  week11: PHASE3_WEEKS[2],
  week12: PHASE3_WEEKS[3],
  phase4a: PHASE4_CONFIGS[0],
  phase4b: PHASE4_CONFIGS[1],
  phase5a: PHASE5_CONFIGS[0],
  phase5b: PHASE5_CONFIGS[1],
  // ✅ REMOVED 2026-04-30 (PR-B): phase5c (sole flag retired). PHASE5_CONFIGS now [5A, 5B, 5D] → 5D shifts to index 2.
  phase5d: PHASE5_CONFIGS[2],
  // ✅ REMOVED 2026-04-30 (PR-D): phase6 (sole flag retired, PHASE6_CONFIGS dropped)
  // 🪦 RETIRED 2026-05-02 (PR-G2): phase7 (PHASE7_CONFIGS removed, 5 Plan flags retired)
  // 🪦 RETIRED 2026-05-04 (PR-G5): phase8 (PHASE8_CONFIGS removed, 6 Monitoring/Validation flags retired)
  phase22: PHASE22_CONFIGS[0]
} as const;

// ========================================
// HELPER FUNCTIONS
// ========================================

export const isHardcodedFlag = (flag: string): boolean => {
  return flag in HARDCODED_FLAGS;
};

export const getAllRolloutFlags = (): string[] => {
  return [
    ...PHASE1_WEEKS.flatMap(w => w.flags),
    ...PHASE2_WEEKS.flatMap(w => w.flags),
    ...PHASE3_WEEKS.flatMap(w => w.flags),
    ...PHASE4_CONFIGS.flatMap(w => w.flags),
    ...PHASE5_CONFIGS.flatMap(w => w.flags),
    // ✅ REMOVED 2026-04-30 (PR-D): ...PHASE6_CONFIGS.flatMap (sole flag retired)
    // 🪦 RETIRED 2026-05-02 (PR-G2): ...PHASE7_CONFIGS.flatMap (Phase 7 fully retired)
    // 🪦 RETIRED 2026-05-04 (PR-G5): ...PHASE8_CONFIGS.flatMap (Phase 8 fully retired)
    ...PHASE22_CONFIGS.flatMap(w => w.flags),
    ...PHASE26_CONFIGS.flatMap(w => w.flags)
  ];
};

export const getWeekForFlag = (flag: string): WeekConfig | undefined => {
  return Object.values(FLAG_WEEK_MAPPING).find(week => 
    week.flags.includes(flag)
  );
};

export const getRolloutProgress = (): {
  phase: number | string;
  enabled: number;
  total: number;
  percentage: number;
}[] => {
  const phases = [
    { phase: 1, weeks: PHASE1_WEEKS },
    { phase: 2, weeks: PHASE2_WEEKS },
    { phase: 3, weeks: PHASE3_WEEKS },
    { phase: '4A/4B', weeks: PHASE4_CONFIGS },
    { phase: '5: Infrastructure', weeks: PHASE5_CONFIGS },
    // ✅ REMOVED 2026-04-30 (PR-D): { phase: '6: Legacy Diary', weeks: PHASE6_CONFIGS } — Phase 6 fully retired
    // 🪦 RETIRED 2026-05-02 (PR-G2): { phase: '7: Plan Optimizations', weeks: PHASE7_CONFIGS } — Phase 7 fully retired
    // 🪦 RETIRED 2026-05-04 (PR-G5): { phase: '8: Admin Enhancements', weeks: PHASE8_CONFIGS } — Phase 8 fully retired
    { phase: '22: User Visibility', weeks: PHASE22_CONFIGS }
  ];

  return phases.map(({ phase, weeks }) => {
    const total = weeks.reduce((sum, w) => sum + w.flags.length, 0);
    // Note: enabled count needs to query globalFeatureFlags at runtime
    return { phase, enabled: 0, total, percentage: 0 };
  });
};

/**
 * Universal checklist template for all weeks
 */
export const UNIVERSAL_CHECKLIST_TEMPLATE = {
  day1: [
    { id: 'flags_enabled', label: 'Flags enabled via Feature Flags UI', auto: false },
    { id: 'no_console_errors', label: 'No console errors on affected pages', auto: false },
    { id: 'basic_functionality', label: 'Basic functionality works as expected', auto: false }
  ],
  day2_3: [
    { id: 'avg_response_time', label: 'Avg Response Time < 200ms', auto: true, threshold: 200, metricKey: 'averageDuration' },
    { id: 'success_rate', label: 'Success Rate > 95%', auto: true, threshold: 95, metricKey: 'successRate' },
    { id: 'cache_hit_rate', label: 'Cache Hit Rate > 80%', auto: true, threshold: 80, metricKey: 'cacheHitRate' },
    { id: 'p95_latency', label: 'P95 Latency < 500ms', auto: true, threshold: 500, metricKey: 'p95' },
    { id: 'zero_errors', label: 'Zero error logs in System Logs', auto: false },
    { id: 'tested_multiple_projects', label: 'Tested with 5+ different projects', auto: false }
  ],
  day4_5: [
    { id: 'metrics_still_green', label: 'All Day 2-3 metrics still green', auto: true },
    { id: 'user_feedback', label: 'User feedback collected (0 complaints)', auto: false },
    { id: 'memory_stable', label: 'Memory usage stable (no leaks)', auto: false }
  ],
  final: [
    { id: 'consecutive_green', label: 'All metrics green for 2 consecutive days', auto: true },
    { id: 'final_validation', label: 'Final validation: All thresholds passed', auto: true },
    { id: 'ready_next_week', label: 'Ready for next week rollout', auto: true }
  ]
};

/**
 * Metric thresholds for auto-validation
 */
export const METRIC_THRESHOLDS = {
  avgResponseTime: { pass: 200, warning: 500, unit: 'ms', operator: '<' },
  successRate: { pass: 95, warning: 90, unit: '%', operator: '>' },
  cacheHitRate: { pass: 80, warning: 60, unit: '%', operator: '>' },
  p95Latency: { pass: 500, warning: 1000, unit: 'ms', operator: '<' },
  renderTime: { pass: 1000, warning: 1500, unit: 'ms', operator: '<' },
  memoryUsage: { pass: 150, warning: 200, unit: 'MB', operator: '<' }
};

// ========================================
// SPECS SYNC PHASE CONFIG
// ========================================
export const PHASE_SPECS_SYNC_CONFIG: WeekConfig = {
  week: 'specs-sync',
  phase: 'phase-specs-sync',
  title: 'Specs Sync by Code',
  risk: 'medium',
  impact: 'Sync specs attributes, shared files, bulk updates',
  flags: ['ENABLE_SPECS_SYNC_BY_CODE'],
  monitoringDays: 14,
  criticalMetrics: [
    'Update latency < 500ms (p95)',
    'Bulk update success rate > 99%',
    'File sync accuracy 100%',
    'Zero data loss'
  ],
  passCriteria: {
    errorRate: '1',
    p95Latency: '500',
    cacheHitRate: '85'
  },
  enableAction: 'enableSpecsSync',
  disableAction: 'disableSpecsSync'
};

// ========================================
// SPECS UI FIXES PHASE CONFIG
// ========================================
export const PHASE_SPECS_UI_FIXES: WeekConfig = {
  week: 'specs-ui',
  phase: 'phase-specs',
  title: 'Specs Report Layout Fix',
  risk: 'low',
  impact: 'Preview ổn định, pins không lệch',
  flags: ['ENABLE_SPECS_REPORT_LAYOUT_FIX'],
  monitoringDays: 3,
  criticalMetrics: ['preview_initial_render < 500ms', 'pin_position_accuracy > 99%'],
  passCriteria: {
    preview_initial_render: '<=500ms',
    pin_position_accuracy: '>=99%'
  },
  enableAction: 'enableSpecsUIFixes',
  disableAction: 'disableSpecsUIFixes'
};

// ========================================
// PHASE 23: PIN PHOTO RLS FIX
// ========================================
export const PHASE23_CONFIGS: WeekConfig[] = [
  {
    week: 'Phase23',
    phase: 'Phase23',
    title: 'Pin Photo RLS Fix',
    risk: 'low',
    impact: 'Fix SM role pin photo upload (403 errors)',
    flags: ['ENABLE_PIN_PHOTO_RLS_V2'],
    monitoringDays: 3,
    criticalMetrics: ['uploadSuccessRate', 'rlsErrorCount'],
    passCriteria: {
      uploadSuccessRate: '>=99%',
      rlsErrorCount: '0'
    },
    enableAction: 'enablePhase23',
    disableAction: 'disablePhase23'
  }
];

// ========================================
// PHASE 24: SPECS ADVANCED FILTERS
// ========================================
export const PHASE24_CONFIGS: WeekConfig[] = [
  {
    week: 'phase24',
    phase: 24,
    title: 'Specs Advanced Filters - Code & Supplier Multi-Select',
    risk: 'medium',
    impact: 'Filter UX: +40%, Pin Sync: 100% accurate',
    flags: ['ENABLE_SPECS_ADVANCED_FILTERS'],
    monitoringDays: 7,
    criticalMetrics: [
      'Filter computation time < 100ms',
      'Pin sync latency < 50ms',
      'Error rate < 1%',
      'Cache hit rate > 85%'
    ],
    passCriteria: {
      filterComputationTime: '<=100ms',
      pinSyncLatency: '<=50ms',
      errorRate: '<=1%',
      cacheHitRate: '>=85%'
    },
    enableAction: 'enablePhase24',
    disableAction: 'disablePhase24'
  }
];

// ========================================
// PHASE 25: SPECS COPY KEYPLAN POSITION
// ========================================
export const PHASE_SPECS_COPY_KEYPLAN_CONFIG: WeekConfig = {
  week: 'specs-copy',
  phase: 'phase-25',
  title: 'Specs Copy Keyplan Position',
  risk: 'low',
  impact: 'Copy spec items to same keyplan position',
  flags: ['ENABLE_SPEC_COPY_KEYPLAN_POSITION'],
  monitoringDays: 3,
  criticalMetrics: ['Copy duration < 2s', 'Error rate < 1%', 'Keyplan copy success > 95%'],
  passCriteria: {
    copyDuration: '<=2000ms',
    errorRate: '<=1%',
    keyplanCopySuccessRate: '>=95%'
  },
  enableAction: 'enableSpecsCopyKeyplan',
  disableAction: 'disableSpecsCopyKeyplan'
};

// ========================================
// PHASE 26: CUSTOM MILESTONES (ITEMS TIMELINE)
// ========================================
export const PHASE26_CONFIGS: WeekConfig[] = [
  {
    week: 'phase26',
    phase: 26,
    title: 'Custom Milestones - Timeline Expand & Filter',
    risk: 'medium',
    impact: 'Timeline: +Milestone management, Filter by type, Expand/collapse rows',
    flags: ['ENABLE_CUSTOM_MILESTONES'],
    monitoringDays: 7,
    criticalMetrics: [
      'Timeline render time < 500ms',
      'Milestone filter < 100ms',
      'Bundle size < +50KB gzipped',
      'Memory increase < 10%'
    ],
    passCriteria: {
      timelineRenderTime: '<=500ms',
      milestoneFilterTime: '<=100ms',
      bundleSizeIncrease: '<=50KB',
      memoryIncrease: '<=10%'
    },
    enableAction: 'enablePhase26',
    disableAction: 'disablePhase26'
  }
];

// ✅ RETIRED 2026-05-01 (PR-F3): PHASE27_CONFIGS removed (ENABLE_PUNCHLIST_LIGHT_V2 hardcoded true)

// ========================================
// PHASE 28: EXECUTE TAB OPTIMIZATION
// ========================================
export const PHASE28_CONFIGS: WeekConfig[] = [
  {
    week: 'phase28',
    phase: 28,
    title: 'Progressive Layer Loading - Execute Tab Optimization',
    risk: 'low',
    impact: 'First layer load: 5-10s → <1s (-80%), Background preload non-blocking',
    flags: ['ENABLE_PROGRESSIVE_LAYER_LOADING'],
    monitoringDays: 7,
    criticalMetrics: [
      'First layer visible: < 1s (down from 5-10s)',
      'Background preload: Complete within 10s',
      'Error rate: < 1%',
      'Memory increase: < 10MB'
    ],
    passCriteria: {
      firstLayerLoad: '<=1s',
      backgroundPreload: '<=10s',
      errorRate: '<1%',
      memoryIncrease: '<=10MB'
    },
    enableAction: 'enablePhase28',
    disableAction: 'disablePhase28'
  }
];

// ✅ RETIRED 2026-05-11 (PR-H1): PHASE_CRM_CONFIG (ENABLE_CRM + ENABLE_CRM_MOCKUP_DATA orphans;
// PR-E previously retired sibling ENABLE_STAKEHOLDER_CRM_DASHBOARD)

// ========================================
// PHASE 29: LAYER IMAGE OPTIMIZATION V2
// ========================================
export const PHASE29_CONFIGS: WeekConfig[] = [
  {
    week: '29',
    phase: '29',
    title: 'Layer Image Optimization V2',
    risk: 'low',
    impact: 'LCP -80%, Mobile bandwidth -90%, Error rate <1%',
    flags: [
      'ENABLE_OPTIMIZED_LAYER_IMAGES'
    ],
    monitoringDays: 7,
    criticalMetrics: ['lcp_p75', 'image_optimization_success_rate', 'error_rate', 'mobile_bandwidth'],
    passCriteria: {
      lcp_p75: '<= 2500ms',
      image_optimization_success_rate: '>= 95%',
      error_rate: '<= 1%',
      mobile_bandwidth: '<= 400KB'
    },
    enableAction: 'enablePhase29',
    disableAction: 'disablePhase29'
  }
];

// ========================================
// SPECS REPORT V2: DIRECT PDF EXPORT
// ========================================
export const PHASE_SPECS_REPORT_V2_CONFIG: WeekConfig = {
  week: 'specs-v2',
  phase: 'specs-v2',
  title: 'Specs Report V2 - Direct PDF Export',
  risk: 'low',
  impact: 'Direct PDF export + 1:1 A4 preview + Enhanced print layout',
  flags: ['ENABLE_SPECS_REPORT_V2'],
  monitoringDays: 7,
  criticalMetrics: [
    'PDF export time < 10s (p95)',
    'Preview render < 1s',
    'Bundle size < +200KB (lazy-loaded)',
    'Error rate < 1%',
    'CORS errors: 0'
  ],
  passCriteria: {
    pdfExportTime: '<=10s',
    previewRender: '<=1s',
    bundleSize: '<=200KB',
    errorRate: '<=1%',
    corsErrors: '0'
  },
  enableAction: 'enableSpecsReportV2',
  disableAction: 'disableSpecsReportV2'
};

// ========================================
// GDRIVE PERMISSION SYNC
// ========================================
export const PHASE_GDRIVE_PERMISSION_SYNC_CONFIG: WeekConfig = {
  week: 'gdrive-sync',
  phase: 'gdrive-permission-sync',
  title: 'Google Drive Permission Sync',
  risk: 'medium',
  impact: 'Auto-sync DB permissions → GDrive roles (Reader/Writer/Commenter)',
  flags: ['ENABLE_GDRIVE_PERMISSION_SYNC'],
  monitoringDays: 7,
  criticalMetrics: [
    'GDrive API error rate < 2%',
    'Sync latency < 5s (10 users)',
    'Quota breach alerts',
    'Permission sync success rate > 90%'
  ],
  passCriteria: {
    apiErrorRate: '<=2%',
    syncLatency: '<=5s',
    quotaBreach: '0',
    syncSuccessRate: '>=90%'
  },
  enableAction: 'enableGDrivePermissionSync',
  disableAction: 'disableGDrivePermissionSync'
};

// ========================================
// API OPTIMIZATION: PAYMENT MILESTONES
// ========================================
// PHASE_API_OPTIMIZATION_CONFIG import directly from '@/config/paymentMilestonesOptimization'
// (re-export removed to break circular dependency)

// ✅ RETIRED 2026-04-30 (PR-F1): PHASE_SERVICE_ITEM_ASSIGNMENT_CONFIG removed (0 importers, flag retired)
